import React, { useState } from 'react';
import { Formik } from 'formik';
import { Button, Radio as RadioElement, Row, Col } from 'antd';

import Step3Result from './Step3Result';
import { formValidator, recommendationData } from './Step3Helpers';
import { Result, Step3 } from '../../../types';
import Select from '../../../common/components/dataEntry/formik/FormikSelectField';
import Radio from '../../../common/components/dataEntry/formik/FormikRadioField';
import Option from '../../../common/components/dataEntry/components/Option';
import Collapse from '../../../common/components/dataEntry/components/Collapse';
import CollapsePanel from '../../../common/components/dataEntry/components/CollapsePanel';
import Label from '../../../common/components/dataEntry/components/Label';

const OPTIN = 'Opt-in';
const OPTOUT = 'Opt-out';
const CONSIDER_YOUR_OPTIONS = 'Consider your options';

const healthInsuranceTypes: string[] = [
  "Spouse's health insurance",
  "Parent's health insurance",
  'Individual health insurance',
  'Short term health insurance',
  'Medicare',
  'Medicaid',
  'Health care sharing plan/ministry',
];

export const Step3DefaultValues = {
  hasInsurance: '',
  healthInsuranceType: '',
  singleAllowance: 0,
};

interface Step3Props {
  onStep3Complete: (values: Step3) => void;
  step3InitialValues: Step3 | null;
  current: number;
  defaultKey: number;
  activeAccordion: number;
  handleCollapse: () => void;
  affordable: boolean;
  onCalulcateRecommendation: (value: string) => void;
  showRecommendation: boolean;
  clearStep: () => void;
}

const Step3Form: React.FC<Step3Props> = ({
  onStep3Complete,
  step3InitialValues,
  current,
  affordable,
  onCalulcateRecommendation,
  showRecommendation,
  clearStep,
  defaultKey,
  activeAccordion,
  handleCollapse,
}) => {
  const [display, setDisplay] = useState<Result | null>(null);

  if (current !== 2) return null;

  const initialFormValues: Step3 = step3InitialValues ?? Step3DefaultValues;

  const onSubmit = async (values: Step3) => {
    const ourRecommendation = calculateRecommendation(values);
    onCalulcateRecommendation(ourRecommendation);
    onStep3Complete(values);
  };

  const calculateRecommendation = (values: Step3): string => {
    let ourRecommendation: string = '';
    if (affordable && values.hasInsurance === 'Yes') {
      switch (values.healthInsuranceType) {
        case 'Individual health insurance':
          ourRecommendation = OPTIN;
          setDisplay(recommendationData['Affordable_IndividualCoverage']);
          break;
        case 'Medicare':
          ourRecommendation = OPTIN;
          setDisplay(recommendationData['Affordable_Medicare']);
          break;
        case "Spouse's health insurance":
          ourRecommendation = OPTOUT;
          setDisplay(recommendationData['Affordable_SpousalCoverage']);
          break;
        case "Parent's health insurance":
          ourRecommendation = OPTOUT;
          setDisplay(recommendationData['Affordable_ParentalCoverage']);
          break;
        case 'Short term health insurance':
          ourRecommendation = OPTIN;
          setDisplay(recommendationData['Affordable_ShortTermCoverage']);
          break;
        case 'Medicaid':
          ourRecommendation = OPTIN;
          setDisplay(recommendationData['Affordable_Medicaid']);
          break;
        case 'Health care sharing plan/ministry':
          ourRecommendation = OPTIN;
          setDisplay(recommendationData['Affordable_HealthSharing']);
          break;
      }
    } else if (affordable && values.hasInsurance === 'No') {
      ourRecommendation = OPTIN;
      setDisplay(recommendationData['Affordable_NoHealthInsurance']);
    } else if (!affordable && values.hasInsurance === 'Yes') {
      switch (values.healthInsuranceType) {
        case 'Individual health insurance':
          ourRecommendation = CONSIDER_YOUR_OPTIONS;
          setDisplay(recommendationData['Unaffordable_IndividualCoverage']);
          break;
        case 'Medicare':
          ourRecommendation = OPTIN;
          setDisplay(recommendationData['Unaffordable_Medicare']);
          break;
        case "Spouse's health insurance":
          ourRecommendation = OPTOUT;
          setDisplay(recommendationData['Unaffordable_SpousalCoverage']);
          break;
        case "Parent's health insurance":
          ourRecommendation = OPTOUT;
          setDisplay(recommendationData['Unaffordable_ParentalCoverage']);
          break;
        case 'Short term health insurance':
          ourRecommendation = CONSIDER_YOUR_OPTIONS;
          setDisplay(recommendationData['Unaffordable_ShortTermCoverage']);
          break;
        case 'Medicaid':
          ourRecommendation = CONSIDER_YOUR_OPTIONS;
          setDisplay(recommendationData['Unaffordable_Medicaid']);
          break;
        case 'Health care sharing plan/ministry':
          ourRecommendation = CONSIDER_YOUR_OPTIONS;
          setDisplay(recommendationData['Unaffordable_HealthSharing']);
          break;
      }
    } else if (!affordable && values.hasInsurance === 'No') {
      ourRecommendation = CONSIDER_YOUR_OPTIONS;
      setDisplay(recommendationData['Unaffordable_NoHealthInsurance']);
    }
    return ourRecommendation;
  };

  return (
    <div>
      <Collapse activeKey={activeAccordion} accordion onChange={handleCollapse}>
        <CollapsePanel
          key={defaultKey}
          header="Our recommendation"
          showArrow={true}
        >
          <Formik
            enableReinitialize={true}
            validationSchema={formValidator}
            initialValues={initialFormValues}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit} autoComplete="off">
                <Label className="formik-field__input-label no-select hand-on-hover mb-8 ml-16 black">
                  Do you have health insurance?
                </Label>
                <Row className="ml-16">
                  <Col xs={12} sm={6} lg={4} xl={3}>
                    <Radio
                      className="radio"
                      value={formik.values.hasInsurance}
                      name="hasInsurance"
                      onRadioChange={(e) => {
                        clearStep();
                      }}
                    >
                      <RadioElement value={'Yes'}>
                        <p>Yes </p>
                      </RadioElement>
                      <RadioElement value={'No'}>
                        <p>No </p>
                      </RadioElement>
                    </Radio>
                  </Col>
                </Row>
                <Label className="formik-field__input-label no-select hand-on-hover mb-8 ml-16 black">
                  Select your type of health insurance:
                </Label>
                <Row className="ml-16">
                  <Col xs={12} sm={6} lg={4} xl={4}>
                    <Select
                      onChange={clearStep}
                      disabled={
                        formik.values.hasInsurance === 'No' ||
                        formik.values.hasInsurance === ''
                      }
                      value={formik.values.healthInsuranceType}
                      name="healthInsuranceType"
                    >
                      {healthInsuranceTypes.map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
                <Row className="ml-16">
                  <Col xs={12} sm={6} lg={3} xl={3}>
                    <Button
                      className="button__large"
                      htmlType="submit"
                      type="primary"
                      disabled={!formValidator.isValidSync(formik.values)}
                    >
                      See recommendation
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        </CollapsePanel>
      </Collapse>
      {showRecommendation && <Step3Result result={display} />}
    </div>
  );
};
export default Step3Form;
